/**
 * The ONE business-timezone date module for WTC.
 *
 * WTC's books run on a Central (`America/Chicago`) wall clock. Storage is UTC,
 * the production Postgres server's own `TimeZone` is UTC, and every Vercel cron
 * fires on a UTC schedule (Vercel crons are UTC-only — there is no `timezone`
 * key in `vercel.json`). So for the last five or six hours of every Central
 * business day, `new Date().toISOString().slice(0, 10)` names TOMORROW.
 *
 * That is not hypothetical. The monthly-invoice cron runs at `0 2 * * *` UTC —
 * 20:00 CDT / 21:00 CST the PREVIOUS Central day — and stamped
 * `TxnDate: today.toISOString().split("T")[0]`, so every invoice was dated a day
 * late and every month-end invoice landed in the next fiscal month. QuickBooks
 * accepts any date it is handed, so nothing ever errored.
 *
 * Two rules this module exists to enforce:
 *
 *  1. A calendar day is derived in the BUSINESS ZONE, never from UTC.
 *  2. A calendar day is advanced with `date + 1` semantics, NEVER `+ 24h`.
 *     Two days a year are 23 and 25 hours long (2026-03-08 and 2026-11-01),
 *     precisely when nobody is looking. `+ 86_400_000` overshoots or undershoots
 *     midnight on those days; `setDate`-on-a-local-Date is worse, because it
 *     silently uses the RUNTIME's zone, which on a Vercel function is UTC.
 *
 * Everything here is pure and injectable (an explicit `Date`, an explicit zone)
 * so a test can pin a clock, and Intl-only so there is no `date-fns-tz` to drift.
 *
 * NOTE on the decoy: `packages/ui/src/lib/date-utils.ts` also mentions
 * `America/Chicago`, but its `parseDateString`/`startOfDay`/`endOfDay` operate in
 * the RUNTIME's local zone. Do not build on it.
 */

/** WTC's operating wall clock (WTC_DEFAULT_TIMEZONE rule). DST-aware, IANA. */
export const WTC_BUSINESS_TIME_ZONE = "America/Chicago";

/** `YYYY-MM-DD`. A calendar day in the business zone, not an instant. */
export type IsoDate = string;

/** `YYYY-MM`. A calendar month in the business zone. */
export type IsoMonth = string;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_HOUR = 3_600_000;

/**
 * The configured business timezone.
 *
 * Reads `WTC_DEFAULT_TIMEZONE` so a deployment can override it, and falls back
 * to Central. Never throws on a bad value — an unparseable zone would take down
 * every invoice run, so it degrades to Central and the caller keeps working.
 */
export function resolveBusinessTimeZone(
  override?: string | undefined
): string {
  // `typeof process` guard: this module is reachable from CLIENT bundles (the
  // funnel payment forms import the invoice builders), where a bare
  // `process.env` read is a ReferenceError in some bundler configurations.
  const fromEnv =
    typeof process !== "undefined"
      ? process.env?.WTC_DEFAULT_TIMEZONE
      : undefined;
  const candidate = override ?? fromEnv ?? WTC_BUSINESS_TIME_ZONE;
  try {
    // Throws RangeError on an invalid IANA identifier.
    new Intl.DateTimeFormat("en-CA", { timeZone: candidate });
    return candidate;
  } catch {
    return WTC_BUSINESS_TIME_ZONE;
  }
}

/** Wall-clock fields of an instant as observed in a timezone. */
export interface BusinessDateParts {
  readonly year: number;
  /** 1-12. */
  readonly month: number;
  /** 1-31. */
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
  /** 0=Sun … 6=Sat. */
  readonly weekday: number;
}

const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Wall-clock parts of `instant` as observed in `timeZone`.
 *
 * `hourCycle: "h23"` matters: without it, midnight formats as `24` in some
 * locales/engines and every start-of-day computation lands a day off.
 */
export function businessDateParts(
  instant: Date,
  timeZone: string = resolveBusinessTimeZone()
): BusinessDateParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
  }).formatToParts(instant);

  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    weekday: WEEKDAY_INDEX[map.weekday ?? "Sun"] ?? 0,
  };
}

/**
 * `YYYY-MM-DD` for an instant, rendered in the business timezone.
 *
 * This is THE replacement for `new Date().toISOString().split("T")[0]`.
 * `en-CA` is used because its short date format IS `YYYY-MM-DD`, which avoids
 * hand-assembling parts and getting month padding wrong.
 */
export function businessDateString(
  instant: Date,
  timeZone: string = resolveBusinessTimeZone()
): IsoDate {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/** Today's calendar day in the business timezone. */
export function todayBusinessDate(
  now: Date = new Date(),
  timeZone: string = resolveBusinessTimeZone()
): IsoDate {
  return businessDateString(now, timeZone);
}

/**
 * `YYYY-MM` for an instant, in the business timezone.
 *
 * Replaces `toISOString().substring(0, 7)`. On the last evening of a month the
 * UTC form names the NEXT month, which is how the recurring sync and the
 * recurring retry came to disagree about what month it was — one stamping
 * invoices in Central, the other filtering `last_synced_month` in UTC.
 */
export function businessMonthString(
  instant: Date,
  timeZone: string = resolveBusinessTimeZone()
): IsoMonth {
  return businessDateString(instant, timeZone).slice(0, 7);
}

/** Parse a `YYYY-MM-DD` into its calendar fields. Throws on a malformed value. */
function parseIsoDate(isoDate: IsoDate): {
  year: number;
  month: number;
  day: number;
} {
  if (!ISO_DATE_RE.test(isoDate)) {
    throw new RangeError(
      `Expected a YYYY-MM-DD business date, received "${isoDate}"`
    );
  }
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  const day = Number(isoDate.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new RangeError(`"${isoDate}" is not a real calendar date`);
  }
  return { year, month, day };
}

/** Render calendar fields back to `YYYY-MM-DD`. */
function formatIsoDate(year: number, month: number, day: number): IsoDate {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
}

/**
 * Advance a calendar day by whole days — `date + N`, NOT `+ N * 24h`.
 *
 * Pure calendar arithmetic on the date STRING, anchored in UTC, which has no
 * DST: adding 1 to 2026-03-07 gives 2026-03-08 whether or not that Central day
 * is 23 hours long. The zone is deliberately not involved — a calendar day is a
 * label, and labels do not shorten in spring.
 */
export function addCalendarDays(isoDate: IsoDate, days: number): IsoDate {
  const { year, month, day } = parseIsoDate(isoDate);
  const anchor = new Date(Date.UTC(year, month - 1, day));
  anchor.setUTCDate(anchor.getUTCDate() + Math.trunc(days));
  return formatIsoDate(
    anchor.getUTCFullYear(),
    anchor.getUTCMonth() + 1,
    anchor.getUTCDate()
  );
}

/**
 * Advance a calendar day by whole months, CLAMPING to the end of the target
 * month rather than overflowing.
 *
 * Jan 31 + 1 month is Feb 28 (or Feb 29), not Mar 3. The native
 * `setMonth`/`setDate(0)` dance the recurring-charge code used got this right by
 * accident and in the RUNTIME's zone; this does it on purpose and in no zone at
 * all.
 */
export function addCalendarMonths(isoDate: IsoDate, months: number): IsoDate {
  const { year, month, day } = parseIsoDate(isoDate);
  const zeroBased = month - 1 + Math.trunc(months);
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth = ((zeroBased % 12) + 12) % 12;
  // Day 0 of the FOLLOWING month is the last day of the target month.
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetYear, targetMonth + 1, 0)
  ).getUTCDate();
  return formatIsoDate(
    targetYear,
    targetMonth + 1,
    Math.min(day, lastDayOfTargetMonth)
  );
}

/** Advance a calendar day by whole years, clamping Feb 29 to Feb 28. */
export function addCalendarYears(isoDate: IsoDate, years: number): IsoDate {
  return addCalendarMonths(isoDate, Math.trunc(years) * 12);
}

/** Offset in ms between `timeZone` wall-clock and UTC at `instant` (DST-aware). */
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = businessDateParts(instant, timeZone);
  const asIfUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asIfUtc - instant.getTime();
}

/**
 * The UTC instant corresponding to a wall-clock time in `timeZone`.
 *
 * Two passes: guess, correct by the offset at the guess, then re-read the offset
 * at the corrected instant. One correction settles every real DST edge.
 *
 * The spring-forward GAP has no correct answer — 02:30 on 2026-03-08 is a wall
 * clock that never occurs in Central. Rather than throw (which would take an
 * invoice run down over a clock change), this resolves to the instant just
 * BEFORE the gap: 02:30 -> 01:30 CST. Documented because it is observable, not
 * because it is meaningful; every WTC caller asks for 00:00 or a business hour,
 * neither of which is ever in the gap.
 */
export function zonedWallClockToUtc(
  year: number,
  /** 1-12. */
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string = resolveBusinessTimeZone()
): Date {
  const guessMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  const corrected = guessMs - timeZoneOffsetMs(new Date(guessMs), timeZone);
  const offset = timeZoneOffsetMs(new Date(corrected), timeZone);
  return new Date(guessMs - offset);
}

/** The UTC instant at which a business calendar day begins (00:00 local). */
export function startOfBusinessDayUtc(
  isoDate: IsoDate,
  timeZone: string = resolveBusinessTimeZone()
): Date {
  const { year, month, day } = parseIsoDate(isoDate);
  return zonedWallClockToUtc(year, month, day, 0, 0, timeZone);
}

/**
 * The UTC instant at which a business calendar day ends — the exclusive upper
 * bound, i.e. the start of the NEXT calendar day.
 *
 * Exclusive on purpose: a half-open `[start, end)` range cannot double-count the
 * boundary instant, and it is derived by advancing the DAY, so it is correct on
 * the 23- and 25-hour days where `start + 24h` is not.
 */
export function endOfBusinessDayUtc(
  isoDate: IsoDate,
  timeZone: string = resolveBusinessTimeZone()
): Date {
  return startOfBusinessDayUtc(addCalendarDays(isoDate, 1), timeZone);
}

/** How many hours long a business calendar day actually is (23, 24, or 25). */
export function businessDayLengthHours(
  isoDate: IsoDate,
  timeZone: string = resolveBusinessTimeZone()
): number {
  const start = startOfBusinessDayUtc(isoDate, timeZone);
  const end = endOfBusinessDayUtc(isoDate, timeZone);
  return (end.getTime() - start.getTime()) / MS_PER_HOUR;
}

/**
 * Do two instants (or ISO dates) fall on the SAME business calendar day?
 *
 * This is the idempotency predicate. Comparing two UTC days for "already charged
 * today" can answer `false` for two charges that are both 21:00 Central on the
 * same business day but straddle UTC midnight — which is a double charge.
 */
export function isSameBusinessDate(
  left: Date | IsoDate,
  right: Date | IsoDate,
  timeZone: string = resolveBusinessTimeZone()
): boolean {
  const normalize = (value: Date | IsoDate): IsoDate =>
    value instanceof Date ? businessDateString(value, timeZone) : value;
  return normalize(left) === normalize(right);
}

/**
 * Net-N payment terms as a business calendar date.
 *
 * `+ N * 24 * 60 * 60 * 1000` on a Net-30 that crosses a DST boundary lands an
 * hour off and can therefore name the wrong day; `date + N` cannot.
 */
export function netTermsDueDate(
  isoDate: IsoDate,
  netDays: number
): IsoDate {
  return addCalendarDays(isoDate, netDays);
}

/**
 * The `TxnDate` / `DueDate` pair every WTC invoice is stamped with.
 *
 * One function so the two dates can never be derived from different clocks —
 * which is exactly what happened when `TxnDate` moved to the business zone in
 * the recurring-sync path while the monthly path kept using UTC.
 */
export interface InvoiceDates {
  readonly txnDate: IsoDate;
  readonly dueDate: IsoDate;
}

export function invoiceDates(
  now: Date = new Date(),
  netDays = 30,
  timeZone: string = resolveBusinessTimeZone()
): InvoiceDates {
  const txnDate = businessDateString(now, timeZone);
  return { txnDate, dueDate: netTermsDueDate(txnDate, netDays) };
}

// ---------------------------------------------------------------------------
// Calendar-date columns: reading, comparing, and DISPLAYING a `YYYY-MM-DD`
// ---------------------------------------------------------------------------

/**
 * The other half of this module's problem, and the more visible one.
 *
 * Postgres `date` columns (`deals.close_date`, `contact_tasks.due_date`,
 * `tickets.due_date`, `time_entries.date`) arrive as the STRING `"2026-06-16"`.
 * `new Date("2026-06-16")` parses that as midnight **UTC** — which is the
 * PREVIOUS DAY in every negative-offset zone, ours included. Two consequences,
 * both shipped and both invisible in code review:
 *
 *   new Date("2026-06-16").toLocaleDateString()   // "6/15/2026" for a US user
 *   new Date(dueDate) < new Date()                // true on the due date itself
 *
 * So a task due today renders a day early AND reads as overdue. The comparison
 * form is worse than a mixed-basis bug, because BOTH halves look reasonable:
 * one side is a UTC instant, the other a local one.
 *
 * The fix is to stop building `Date` objects for this at all. A calendar date is
 * not an instant; comparing two `YYYY-MM-DD` strings lexicographically is exact,
 * zone-free, and cannot drift. `compareCalendarDates` and the predicates below
 * do that, and `formatCalendarDate` renders the day that was actually stored.
 */

/** True when `value` is a well-formed `YYYY-MM-DD`. */
export function isIsoDate(value: unknown): value is IsoDate {
  return typeof value === "string" && ISO_DATE_RE.test(value);
}

/**
 * The calendar day named by a date column, or `null` when there isn't one.
 *
 * Accepts the `YYYY-MM-DD` a `date` column yields, and tolerates a full
 * timestamp by taking its date part — some columns are `timestamptz` and some
 * callers have already stringified. Anything else (empty, malformed, a bare
 * year) is `null` rather than a guess: a wrong day is worse than a missing one.
 */
export function toCalendarDate(value: string | null | undefined): IsoDate | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const datePart = trimmed.slice(0, 10);
  return ISO_DATE_RE.test(datePart) ? datePart : null;
}

/**
 * `-1 | 0 | 1`, comparing two calendar days.
 *
 * Lexicographic on `YYYY-MM-DD` IS chronological — fixed-width, zero-padded,
 * most-significant-first — so this needs no zone, no parsing, and no `Date`.
 */
export function compareCalendarDates(a: IsoDate, b: IsoDate): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * True when `date` is strictly BEFORE today in the business zone — i.e. overdue.
 *
 * "Today" is deliberately the business day, not the server's: a Vercel function
 * runs in UTC, so a UTC-derived "today" names tomorrow for the last five or six
 * hours of every Central day and would flip due-today items to overdue each
 * evening.
 *
 * A null/malformed date is NOT overdue — nothing is known about it.
 */
export function isCalendarDateOverdue(
  value: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = resolveBusinessTimeZone()
): boolean {
  const date = toCalendarDate(value);
  if (date === null) return false;
  return compareCalendarDates(date, todayBusinessDate(now, timeZone)) < 0;
}

/** True when `date` is today in the business zone. */
export function isCalendarDateToday(
  value: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = resolveBusinessTimeZone()
): boolean {
  const date = toCalendarDate(value);
  if (date === null) return false;
  return date === todayBusinessDate(now, timeZone);
}

/** True when `date` is today or later in the business zone (i.e. not yet due). */
export function isCalendarDateTodayOrFuture(
  value: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = resolveBusinessTimeZone()
): boolean {
  const date = toCalendarDate(value);
  if (date === null) return false;
  return compareCalendarDates(date, todayBusinessDate(now, timeZone)) >= 0;
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function calendarDaysBetween(from: IsoDate, to: IsoDate): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  // Both endpoints are read as UTC midnight, so the offset cancels and DST
  // cannot skew the difference — the one place a UTC instant is the RIGHT tool.
  const aUtc = Date.UTC(a.year, a.month - 1, a.day);
  const bUtc = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bUtc - aUtc) / 86_400_000);
}

/** Whole calendar days until `date` from today in the business zone. Null when unparseable. */
export function calendarDaysUntil(
  value: string | null | undefined,
  now: Date = new Date(),
  timeZone: string = resolveBusinessTimeZone()
): number | null {
  const date = toCalendarDate(value);
  if (date === null) return null;
  return calendarDaysBetween(todayBusinessDate(now, timeZone), date);
}

/**
 * Render a calendar date for a human, on the day it actually names.
 *
 * Uses `Date.UTC` + a UTC-pinned formatter rather than a local `Date`, so the
 * output is the stored day in every runtime zone. `new Date(iso).toLocaleDateString()`
 * is the bug this replaces — it shows the previous day to every US user.
 *
 * A calendar date has no zone, so it is NOT converted into one: a deal closing
 * `2026-06-16` reads "Jun 16, 2026" in Memphis and in Berlin.
 */
export function formatCalendarDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" },
  locale = "en-US"
): string | null {
  const date = toCalendarDate(value);
  if (date === null) return null;
  const { year, month, day } = parseIsoDate(date);
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, day))
  );
}
